import React, { useCallback, useMemo, useEffect, type PropsWithChildren } from 'react';
import { graphql, useFragment, type EntryPointProps, type PreloadedQuery } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@jira/navigation-apps__sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { useShowRoadmapsUpsell as useShowRoadmapsUpsellNew } from '@jira/growth__jira-roadmap-in-nav/src/useShowRoadmapsUpsell.tsx';
import type { useShowRoadmapsUpsell$key } from '@jira/growth__jira-roadmap-in-nav/src/__generated__/useShowRoadmapsUpsell.graphql.ts';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import {
	USER_NAVIGATION_CONFIGURATION_DEFAULT,
	USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx';
import { extractPermissionResults } from '@jira/itam__tenant-context/src/extractPermissionResults.tsx';
import { extractWorkspaceAri } from '@jira/itam__tenant-context/src/extractWorkspaceAri.tsx';
import { RECENT_QUERY_LIMIT } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';
import {
	FAVOURITE_QUERY_LIMIT,
	VALID_FAVORITE_TYPES,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/common/constants.tsx';
import { useFilterMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/common/controllers/use-filter-menu-items/index.tsx';
import { useResolveInvitedAppShortcuts } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/ui/configurable-menu-items/global-shortcuts/useResolveInvitedAppShortcuts.tsx';
import { Nav4JiraSideNavRecommendation } from '@jira/growth__side-nav-recommendation/src/index.tsx';
import { MENU_ID_FOR_YOU } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useShowItam } from '@jira/itam__tenant-context/src/useShowItam.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { UseInvitedAppShortcutsResult } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import type {
	GlobalSideNavigationProps,
	ShortcutsConfig,
} from '@atlassian/global-side-navigation/common/types';
import type { ForYouConfig } from '@atlassian/global-side-navigation/for-you-types';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import { GSNForContentSidebarWithInvitedAppRecommendations } from './GSNForContentSidebarWithInvitedAppRecommendations.tsx';
import { getInvitedAppShortcutsRecommendationsResult } from './getInvitedAppShortcutsRecommendationsResult.tsx';
import { getForYouConfig } from './getForYouConfig.tsx';
import {
	InvitedAppShortcutsSlot,
	InvitedAppShortcutsSlotContext,
	type InvitedAppShortcutsSlotConfig,
} from './InvitedAppShortcutsSlot.tsx';
import { useShortcuts } from './useShortcuts.tsx';
import { useItemsConfig } from './useItemsConfig.tsx';
import { GSNQueryPreloader } from './GSNQueryPreloader.tsx';
import type {
	GSNQueryPreloaderQuery,
	GSNQueryPreloaderQuery$data,
} from './__generated__/GSNQueryPreloaderQuery.graphql.ts';
import { useNetworkStorageParams } from './useNetworkStorageParams.tsx';
import { useSaveCurrentContentSidebarRoute } from './useSaveCurrentContentSidebarRoute.tsx';
import { SIDEBAR_TEST_ID } from './sidebarTestId.constants.tsx';
import { usePeekMenuId } from './usePeekMenuId.tsx';
import type { GSNForContentSidebarEntryPoint$key } from './__generated__/GSNForContentSidebarEntryPoint.graphql.ts';

type EntryPointParams = EntryPointProps<
	// gsnSidebarContent is optional in the entrypoint type because anonymous users receive an empty
	// queries object. However, NavPlatformSidebar (the parent) ensures this component is never
	// rendered for anonymous users, so gsnSidebarContent is always defined here at runtime.
	{
		gsnSidebarContent?: GSNQueryPreloaderQuery;
	},
	{},
	{},
	{ isShortcutsStatic?: boolean } | null
>;

export function GSNForContentSidebarEntryPoint(props: EntryPointParams): React.JSX.Element {
	// This EP has the preloaded query-ref, to pass to the component that will usePreloadedQuery().
	// NavPlatformSidebar guarantees this component is only rendered for authenticated users,
	// so gsnSidebarContent is always defined (non-null assertion is safe here).
	return (
		<GSNForContentSidebar
			// NavPlatformSidebar guarantees this component is only rendered for authenticated users,
			// so gsnSidebarContent is always defined at runtime. Cast is safe.
			// oxlint-disable-next-line typescript/consistent-type-assertions
			queryReference={props.queries.gsnSidebarContent as PreloadedQuery<GSNQueryPreloaderQuery>}
			isShortcutsStatic={props.extraProps?.isShortcutsStatic}
		/>
	);
}
export default GSNForContentSidebarEntryPoint;

interface Props {
	queryReference: PreloadedQuery<GSNQueryPreloaderQuery>;
	isShortcutsStatic?: boolean;
	/**
	 * Allow this provider to be mocked (Storybook has its own)
	 * @private
	 * @deprecated - The provider now appears higher in the component tree (around the NavPlatformSidebar),
	 *               so there is no need to mock it here.
	 */
	MaybeSidebarNav4ContextProvider?: React.FC<PropsWithChildren>;
}

export function GSNForContentSidebar(props: Props): React.JSX.Element {
	// Add support for the Back button inside non-content sidebars, by storing the previous URL when the content sidebar is showing.
	// To test this component, make sure that it is wrapped in NavigateBackProvider
	useSaveCurrentContentSidebarRoute();

	const renderFn = useCallback(
		({ queryReference }: { queryReference: GSNQueryPreloaderQuery$data }) => (
			<GSNForContentSidebarInner
				queryReference={queryReference}
				isShortcutsStatic={props.isShortcutsStatic}
			/>
		),
		[props.isShortcutsStatic],
	);

	return <GSNQueryPreloader queryReference={props.queryReference} render={renderFn} />;
}

interface InnerProps {
	isShortcutsStatic?: boolean;
	queryReference: GSNForContentSidebarEntryPoint$key;
}

const useShowRoadmapsUpsell = functionWithCondition(
	() => fg('jpd-roadmaps-in-jira'),
	(data: useShowRoadmapsUpsell$key) => useShowRoadmapsUpsellNew(data),
	() => false,
);

const useShowItamWithFg = functionWithCondition(
	() => fg('itam_ftw'),
	(data: {
		readonly assetsVertical_tenantContext?: Parameters<typeof extractWorkspaceAri>[0];
		readonly assetsVertical_checkPermissions?: Parameters<typeof extractPermissionResults>[0];
	}) => {
		const assetsVerticalWorkspaceAri = extractWorkspaceAri(data.assetsVertical_tenantContext);
		const staticResults = extractPermissionResults(data.assetsVertical_checkPermissions);
		const { showItam } = useShowItam({ assetsVerticalWorkspaceAri, staticResults });
		return showItam;
	},
	() => false,
);

// This is the component that should be referenced in Storybook.
// Each sidebar can have its own overrides (e.g. isShortcutsStatic)
function GSNForContentSidebarInner(props: InnerProps) {
	const { isShortcutsStatic = false, queryReference } = props;

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment(
		// Need to disable this rule for fragments that are used by child components
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		/* eslint-disable @atlassian/relay/unused-fields */
		graphql`
			fragment GSNForContentSidebarEntryPoint on Query {
				...useNetworkSideNavUserData
				...useShowRoadmapsUpsell @dangerously_unaliased_fixme @include(if: $isRoadmapUpsellEnabled)
				...useGlobalShortcutsDataRelay
					@dangerously_unaliased_fixme
					@include(if: $fetchGlobalShortcutsAvailableProducts)
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				...Nav4Apps
				...Nav4Filters
				...Nav4Operations
				...Nav4Plans
				...Nav4Projects @arguments(currentURL: $currentURL)
				...Nav4Roadmaps_query
				assetsVertical_tenantContext(cloudId: $cloudId)
					@optIn(to: "AssetsVerticalTenantContext")
					@include(if: $isItamEnabled) {
					__typename
					... on AssetsVerticalTenantContextSuccess {
						tenantContext {
							assetsVerticalWorkspaceAri
						}
					}
				}
				assetsVertical_checkPermissions(cloudId: $cloudId, checks: $itamStaticChecks)
					@optIn(to: "AssetsVerticalCheckPermissions")
					@include(if: $isItamEnabled) {
					__typename
					... on AssetsVerticalCheckPermissionsSuccess {
						results {
							resourceId
							permissionId
							permitted
						}
					}
				}
				assetsVertical_verticalInstantiations(cloudId: $cloudId, first: 100, type: HAM)
					@optIn(to: "AssetsVerticalVerticalInstantiations")
					@include(if: $isItamEnabled) {
					edges {
						node {
							id
							type
							status
						}
					}
				}
				growthRecommendations @include(if: $shouldFetchInvitedAppShortcuts) {
					recommendations(
						context: {
							tenantId: $cloudId
							product: $invitedAppShortcutsProduct
							useCase: "invitedAppShortcuts"
						}
					) {
						__typename
						... on GrowthRecRecommendations {
							data {
								entityId
								reasons
								score
							}
							context {
								tenantId
								product
								useCase
								sessionId
							}
						}
						... on QueryError {
							identifier
							message
							extensions {
								statusCode
								errorType
							}
						}
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryReference,
	);

	const networkStorageParamsPartial = useNetworkStorageParams();
	const networkStorageParams = useMemo(() => {
		return { ...networkStorageParamsPartial, queryRef: data };
	}, [data, networkStorageParamsPartial]);
	const { siteId: cloudId } = networkStorageParams;
	const { isSelectedPath } = useSidebarNav4(); // This is needed to support peeking / peekMenuId

	// Items ------
	const shouldShowRoadmapsUpsell = useShowRoadmapsUpsell(data);

	const showItam = useShowItamWithFg(data);

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		expVal('navx-1982-change-order-of-teams-and-goals', 'isEnabled', false)
			? USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED.navItems
			: USER_NAVIGATION_CONFIGURATION_DEFAULT.navItems,
		{ shouldShowRoadmapsUpsell, showItam },
	);

	const items = useItemsConfig(navItems, data);

	// Shortcuts
	const shortcuts = useShortcuts();

	// ForYou config --------
	const recentEntryPointParams = useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]);
	const starredEntryPointParams = useMemo(
		() => ({
			cloudId,
			filter: { types: VALID_FAVORITE_TYPES, sort: { order: 'DESC' as const } },
			count: FAVOURITE_QUERY_LIMIT,
		}),
		[cloudId],
	);
	const { formatMessage } = useIntl();
	const starredFilterMenuItemDataRaw = useFilterMenuItems();
	const starredFilterConfig = useMemo(
		() =>
			starredFilterMenuItemDataRaw.map((item) => ({
				key: item.filterType,
				label: formatMessage(item.message),
				elemBefore: <item.Icon />,
			})),
		[formatMessage, starredFilterMenuItemDataRaw],
	);

	const isForYouSelected = isSelectedPath(MENU_ID_FOR_YOU);
	const forYouConfig = useMemo(
		() =>
			// oxlint-disable-next-line typescript/consistent-type-assertions
			getForYouConfig({
				isForYouSelected,
				recentEntryPointParams,
				starredEntryPointParams,
				starredFilterConfig,
			}) as ForYouConfig<ObjectListFlyoutItem>,
		[isForYouSelected, recentEntryPointParams, starredEntryPointParams, starredFilterConfig],
	);

	// Peek Menu ------
	// Determine peekMenuId from selectedPath
	const peekMenuIdWithRouteFilter = usePeekMenuId(navItems);
	const peekMenuId = expVal('uxissue-1664-autoexpand-spaces', 'isEnabled', false)
		? peekMenuIdWithRouteFilter
		: navItems.find((item) => isSelectedPath(item.menuId))?.menuId;

	// Note: When Jira GSN adoption rolls out, it should rollout with "network-migration" for 3 months
	//  to give time for all old user settings to be stored in the new records.
	//
	//    - customizationStore="network-migration"
	//
	//  After 3 months of being rolled-out, create a new FG to:
	//   - Change customizationStore to "network"
	//   - @include(if: $fgVariable) for useNetworkSideNavUserData.
	//   - After that has rolled out, cleanup
	// `as const` so it matches the literal-union type
	// (`'network-migration' | 'local' | 'network'`) expected downstream.
	const customizationStore = 'network-migration' as const;

	const bottomElem = useMemo(() => <Nav4JiraSideNavRecommendation />, []);

	// `as const` so it matches the `'inherit'` literal type expected downstream.
	const intlMode = 'inherit' as const;

	// `useAccountId` at the top of the hook order (Rules of Hooks).
	const userId = useAccountId();

	// TWCG-460 productionisation dispatch. The recommendations are fetched as
	// part of the GSN query behind `@include(if: $shouldFetchInvitedAppShortcuts)`
	// (the kill-switch gate + experiment, computed where the query variables are
	// built). So `data.growthRecommendations` is present ONLY for the test cohort;
	// deriving dispatch from the field presence keeps it consistent across SSR and
	// hydration (the include decision is fixed at preload). Not read from a live
	// `expVal` here — that would risk an SSR<->client hydration mismatch and would
	// expose before the treatment shows (exposure fires in `useIsInvitedAppshortcutsEnabled`).
	const willDispatchProductionisation = Boolean(
		cloudId && userId && data.growthRecommendations !== undefined,
	);

	// Build the translated recommendations result from the preloaded field.
	// `undefined` (field not included / control) yields an empty result, which
	// the productionisation wrapper treats as NO_RECOMMENDED_APPS.
	const invitedAppShortcutsRecommendationsResult = useMemo(
		() => getInvitedAppShortcutsRecommendationsResult(data.growthRecommendations),
		[data.growthRecommendations],
	);

	// Shortcuts Experiment -----
	const [isInvitedAppShortcutsEnabled, invitedAppShortcutsExperiment, onShortcutsMenuSettled] =
		useIsInvitedAppshortcutsEnabled();

	// Config for the `ShortcutsSlot` (experiment/legacy branch). Passed to the
	// module-level `InvitedAppShortcutsSlot` via context so the slot keeps a
	// stable identity and GSN never remounts the shortcuts subtree. Memoised to
	// keep the context value referentially stable across renders.
	const experimentSlotConfig = useMemo<InvitedAppShortcutsSlotConfig>(
		() => ({ mode: 'experiment', resolverResult: invitedAppShortcutsExperiment }),
		[invitedAppShortcutsExperiment],
	);

	// Stable forwarded prop bag for `GSNForContentSidebarWithInvitedAppRecommendations`.
	// Memoised to satisfy `@atlassian/perf-linting/no-unstable-inline-props`
	// and keep downstream `React.memo` identity stable. Runs unconditionally
	// to keep hook order stable.
	// `userId ?? ''` is type-narrowing only: the consuming JSX is gated by
	// `willDispatchProductionisation` which already requires `userId` to be truthy.
	const invitedAppRecommendationsContainerProps = useMemo(
		() => ({
			bottomElem,
			cloudId,
			customizationStore,
			experimental_onShortcutsMenuSettled: onShortcutsMenuSettled,
			forYouConfig,
			intlMode,
			isShortcutsStatic,
			items,
			networkStorageParams,
			peekMenuId,
			shortcuts,
			testId: SIDEBAR_TEST_ID,
			userId: userId ?? '',
		}),
		[
			bottomElem,
			cloudId,
			customizationStore,
			onShortcutsMenuSettled,
			forYouConfig,
			intlMode,
			isShortcutsStatic,
			items,
			networkStorageParams,
			peekMenuId,
			shortcuts,
			userId,
		],
	);

	// Render the legacy experiment branch or the plain control. Used both as
	// the regular fallthrough and as the productionisation `JSErrorBoundary`
	// fallback (so a transport error on the recommendations query degrades to
	// the same sidebar the gate-off cohort would see).
	const renderLegacyOrControlSidebar = useCallback((): React.JSX.Element => {
		// GSN always receives the base `shortcuts` config. When the invited-app
		// shortcuts experiment is enabled, the decoration happens inside GSN's
		// `ShortcutsSlot` (`InvitedAppShortcutsSlot`) rather than by swapping the
		// whole `shortcuts` prop, so SSR<->CSR flips of the experiment no longer
		// restructure the nav tree.
		const sharedProps: GlobalSideNavigationProps = {
			app: 'jira' as const,
			appLabel: 'Jira',
			bottomElem,
			cloudId,
			customizationStore,
			forYou: forYouConfig,
			intlMode,
			isShortcutsStatic, // Needed for VR tests as the URL can't be mocked
			items,
			networkStorageParams,
			peekMenuId,
			shortcuts,
			testId: SIDEBAR_TEST_ID,
			...(fg('twcg-460-personalised-invite-notifications-m2-gate') && {
				experimental_onShortcutsMenuSettled: onShortcutsMenuSettled,
			}),
		};

		if (isInvitedAppShortcutsEnabled) {
			return (
				<InvitedAppShortcutsSlotContext.Provider value={experimentSlotConfig}>
					<GlobalSideNavigation {...sharedProps} ShortcutsSlot={InvitedAppShortcutsSlot} />
				</InvitedAppShortcutsSlotContext.Provider>
			);
		}

		return <GlobalSideNavigation {...sharedProps} />;
	}, [
		bottomElem,
		cloudId,
		customizationStore,
		experimentSlotConfig,
		forYouConfig,
		intlMode,
		isInvitedAppShortcutsEnabled,
		isShortcutsStatic,
		items,
		networkStorageParams,
		onShortcutsMenuSettled,
		peekMenuId,
		shortcuts,
	]);

	// Productionisation render: the recommendations were preloaded as part of the
	// GSN query (test cohort only). `ContextualAnalyticsData` is required so the
	// `JSErrorBoundary` can emit analytics when it catches (per the AFM
	// `handling-errors` skill); the boundary fails open to the legacy / control
	// branch so a transport error on the recommendations doesn't take down the
	// sidebar.
	if (willDispatchProductionisation) {
		return (
			<ContextualAnalyticsData menuId="gsn-invited-app-shortcuts-productionisation">
				<JSErrorBoundary
					id="gsn-invited-app-shortcuts-productionisation"
					packageName="jiraNavPlatformIntegrationSidebar"
					teamName="growth-puma"
					fallback={renderLegacyOrControlSidebar}
				>
					<GSNForContentSidebarWithInvitedAppRecommendations
						{...invitedAppRecommendationsContainerProps}
						result={invitedAppShortcutsRecommendationsResult}
					/>
				</JSErrorBoundary>
			</ContextualAnalyticsData>
		);
	}

	return renderLegacyOrControlSidebar();
}

function useIsInvitedAppshortcutsEnabled(): [
	boolean,
	UseInvitedAppShortcutsResult,
	(input: { shortcuts: ShortcutsConfig; visibleMenuIds: string[] }) => void,
] {
	const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();

	const isEligibleToSeeInvitedAppShortcuts =
		// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('twcg-460-personalised-invite-notifications-m2-gate') &&
		invitedAppShortcutsExperiment.ineligibilityReason === null &&
		invitedAppShortcutsExperiment.invitedApps?.confluence?.eligible === true;

	const isInvitedAppShortcutsEnabled =
		isEligibleToSeeInvitedAppShortcuts &&
		expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false);

	useEffect(() => {
		if (isEligibleToSeeInvitedAppShortcuts) {
			// Expose the productionisation-preload experiment across the WHOLE
			// kill-switch cohort — both arms — so the A/B is balanced. The gate
			// (not `willDispatchProductionisation`) is the enrolment signal:
			// `willDispatchProductionisation` is true only for treatment (control's
			// mount is experiment-gated), so keying on it would only ever emit
			// `variant`. Exposure is deliberately decoupled from the render/data
			// source — treatment renders AGG recs, control keeps rendering from the
			// cookie cache — while `expVal` resolves the arm at render time on the
			// client. Gate-off users fall through to the legacy m2 exposure.
			if (fg('twcg-460-preload-grs-invited-app-shortcuts-gate')) {
				invitedAppShortcutsExperiment.fireFeatureExposed({
					isEnabled: expVal('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', false),
					overrideMode: 'productionisation',
				});
			} else {
				invitedAppShortcutsExperiment.fireFeatureExposed({
					isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
				});
			}
		}
	}, [isEligibleToSeeInvitedAppShortcuts, invitedAppShortcutsExperiment]);

	const onShortcutsMenuSettled = useCallback(
		({
			shortcuts: hydratedShortcuts,
			visibleMenuIds,
		}: {
			shortcuts: ShortcutsConfig;
			visibleMenuIds: string[];
		}) => {
			invitedAppShortcutsExperiment.updateFromHydratedShortcuts({
				shortcuts: hydratedShortcuts,
				limitToOnlyApsKeys: ['CONFLUENCE'],
				visibleMenuIds,
			});
		},
		[invitedAppShortcutsExperiment],
	);

	return [isInvitedAppShortcutsEnabled, invitedAppShortcutsExperiment, onShortcutsMenuSettled];
}
